import { forwardRef, useImperativeHandle, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { WooCommerceApiAdapter } from '../../adapters/WooCommerceApiAdapter';
import { useAuth } from '@modules/Auth';

export interface WooCommerceSettingsModalRef {
  open: () => void;
}

interface WooCommerceSettingsModalProps {
  onShopCreated?: () => void; // 🎯 Callback para refrescar la lista de tiendas
}

export const WooCommerceSettingsModal = forwardRef<WooCommerceSettingsModalRef, WooCommerceSettingsModalProps>(
  ({ onShopCreated }, ref) => {
    const { t } = useTranslation('shopsModule');
    const { toast } = useToast();
    const { currentUser, isLoadingUser } = useAuth();
    const modalRef = useRef<ModalCenterRef>(null);
    const [formData, setFormData] = useState({
      shopName: '',
      shopUrl: '',
      consumerKey: '',
      consumerSecret: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    // 🎯 Inicializar adapter para WooCommerce
    const wooCommerceAdapter = new WooCommerceApiAdapter(createHttpClient(apiClientWH));

    const isFormValid = useMemo(() => {
      return Object.values(formData).every((value) => value.trim() !== '');
    }, [formData]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setFormData({
          shopName: '',
          shopUrl: '',
          consumerKey: '',
          consumerSecret: '',
        });
        modalRef.current?.openModal();
      },
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.target;
      setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSave = async () => {
      if (!isFormValid) return;

      setIsLoading(true);

      try {
        // 🎯 Preparar datos para el adapter
        const shopData = {
          name: formData.shopName,
          channel_id: 3, // WooCommerce
          connect_data: {
            url: formData.shopUrl,
            client_key: formData.consumerKey,
            client_secret: formData.consumerSecret,
          },
        };

        // 🎯 Llamar al adapter para crear la tienda
        const response = await wooCommerceAdapter.createWooCommerceShop(shopData);

        if (response.success) {
          // 🎯 Mostrar toast de éxito
          toast({
            title: t('modals.wooCommerce.toast.success.title'),
            description: response.message,
            variant: 'default',
          });

          // 🎯 Cerrar modal y limpiar formulario
          modalRef.current?.closeModal();
          setFormData({
            shopName: '',
            shopUrl: '',
            consumerKey: '',
            consumerSecret: '',
          });

          // 🎯 Refrescar la lista de tiendas si es necesario
          onShopCreated?.();
        } else {
          // 🎯 Mostrar toast de error
          toast({
            title: t('modals.wooCommerce.toast.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        // 🎯 Manejar errores inesperados
        toast({
          title: t('modals.wooCommerce.toast.error.title'),
          description: t('modals.wooCommerce.toast.error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <ModalCenter ref={modalRef} title={t('modals.wooCommerce.title')} type={{ isBlocking: true }}>
        <div className="space-y-6 max-h-[calc(100vh-100px)] overflow-y-auto ">
          <Alert className="bg-body-bg mt-2">
            <div className="flex flex-col md:flex-row items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faCircleInfo} className="w-5 h-5" />
              <AlertTitle className="text-lg">{t('modals.wooCommerce.alert.title')}</AlertTitle>
            </div>

            <AlertDescription className="text-icons text-sm">
              {t('modals.wooCommerce.alert.description')}
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shopName">{t('modals.wooCommerce.labels.shopName')}</Label>
              <Input
                id="shopName"
                value={formData.shopName}
                onChange={handleChange}
                placeholder={t('modals.wooCommerce.placeholders.shopName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shopUrl">{t('modals.wooCommerce.labels.shopUrl')}</Label>
              <Input
                id="shopUrl"
                value={formData.shopUrl}
                onChange={handleChange}
                placeholder={t('modals.wooCommerce.placeholders.shopUrl')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="consumerKey">{t('modals.wooCommerce.labels.consumerKey')}</Label>
              <Input
                id="consumerKey"
                value={formData.consumerKey}
                onChange={handleChange}
                placeholder={t('modals.wooCommerce.placeholders.consumerKey')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="consumerSecret">{t('modals.wooCommerce.labels.consumerSecret')}</Label>
              <Input
                id="consumerSecret"
                value={formData.consumerSecret}
                onChange={handleChange}
                placeholder={t('modals.wooCommerce.placeholders.consumerSecret')}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="primary" onClick={handleSave} disabled={!isFormValid || isLoading}>
              {isLoading ? t('modals.wooCommerce.buttons.saving') : t('modals.wooCommerce.buttons.save')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

WooCommerceSettingsModal.displayName = 'WooCommerceSettingsModal';
